/* ============================================================
   MORFYLO — theme.js
   Panier AJAX (Section Rendering) · tiroir accessible · buybox 1/2/3
   · sticky ATC · galerie · header · menu mobile · révélations.
   Vanilla JS, zéro dépendance. Progressive enhancement : tout
   fonctionne sans JS (formulaires natifs), JS améliore.
   ============================================================ */
(() => {
  'use strict';

  const MF = window.MF || {};
  const routes = MF.routes || {};
  const cfg = MF.settings || {};
  const STR = MF.strings || {};
  /* Les chaînes Liquid arrivent parfois avec des entités HTML (&#39;) : on les décode une fois. */
  const decode = (str) => { const t = document.createElement('textarea'); t.innerHTML = str; return t.value; };
  for (const k of Object.keys(STR)) if (typeof STR[k] === 'string') STR[k] = decode(STR[k]);
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ═══ HELPERS ═══ */
  const qs = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => Array.from(c.querySelectorAll(s));

  /**
   * Formate des centimes selon shop.money_format (ex. « 104,85 € »).
   * @param {number} cents
   * @returns {string}
   */
  const formatMoney = (cents) => {
    const fmt = cfg.moneyFormat || '{{amount_with_comma_separator}} €';
    const value = (Math.round(cents) / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const intFr = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    let s = `${intFr},${dec}`;
    if (/no_decimals/.test(fmt)) s = intFr;
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/g, s).replace(/<[^>]+>/g, '');
  };

  /* ═══ TOAST MOMO ═══ */
  let toastEl;
  let toastTimer;
  const toast = (title, sub) => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.innerHTML = `<div data-momo="wave" data-size="56" style="--momo-size:56px"><img class="momo__body" src="${MF.momoBody || ''}" alt="" width="588" height="640"></div><div><b data-toast-title></b><span data-toast-sub></span></div>`;
      document.body.appendChild(toastEl);
      document.dispatchEvent(new CustomEvent('mf:momo:mount'));
    }
    toastEl.querySelector('[data-toast-title]').textContent = title;
    const subEl = toastEl.querySelector('[data-toast-sub]');
    subEl.textContent = sub || '';
    subEl.hidden = !sub;
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
  };

  /* ═══ FOCUS TRAP (tiroir + menu) ═══ */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let trapHandler = null;
  let lastTrigger = null;
  const trapFocus = (container) => {
    releaseFocus();
    trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const items = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', trapHandler);
    const first = qsa(FOCUSABLE, container).find((el) => el.offsetParent !== null);
    if (first) first.focus({ preventScroll: true });
  };
  const releaseFocus = () => {
    if (trapHandler) document.removeEventListener('keydown', trapHandler);
    trapHandler = null;
  };

  /* ═══ TIROIR PANIER ═══ */
  const openDrawer = (trigger) => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    lastTrigger = trigger || document.activeElement;
    qs('[data-cart-overlay]')?.classList.add('is-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('body-lock');
    setTimeout(() => trapFocus(drawer), 60);
  };
  const closeDrawer = () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer || !drawer.classList.contains('is-open')) return;
    qs('[data-cart-overlay]')?.classList.remove('is-open');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('body-lock');
    releaseFocus();
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus({ preventScroll: true });
  };

  let lastCount = null;
  const syncCount = () => {
    const src = qs('[data-cart-total-count]');
    const n = src ? parseInt(src.textContent, 10) || 0 : 0;
    const grew = lastCount !== null && n > lastCount;
    for (const btn of qsa('[data-cart-open][data-cart-label]')) btn.setAttribute('aria-label', `${btn.getAttribute('data-cart-label')} (${n})`);
    for (const el of qsa('[data-cart-count]')) {
      el.textContent = n;
      el.classList.toggle('is-visible', n > 0);
      if (grew && !reduced) {
        const btn = el.closest('.icon-btn');
        if (btn) { btn.classList.remove('is-bump'); void btn.offsetWidth; btn.classList.add('is-bump'); }
      }
    }
    lastCount = n;
  };

  const replaceDrawer = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const fresh = tmp.querySelector('[data-cart-drawer-inner]');
    const live = qs('[data-cart-drawer-inner]');
    if (fresh && live) live.innerHTML = fresh.innerHTML;
    syncCount();
    document.dispatchEvent(new CustomEvent('mf:cart:updated'));
  };

  /**
   * Requête panier avec rendu de la section « cart-drawer ».
   * @param {string} url
   * @param {object} body
   */
  const cartRequest = async (url, body) => {
    body.sections = 'cart-drawer';
    body.sections_url = window.location.pathname;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    return { ok: res.ok, status: res.status, data };
  };

  const addItem = async (id, quantity, opts = {}) => {
    try {
      const res = await cartRequest(`${routes.cart_add}.js`, { items: [{ id, quantity: quantity || 1 }] });
      if (!res.ok) { toast(res.data?.description || STR.addError || 'Erreur panier'); return res; }
      if (res.data.sections?.['cart-drawer']) replaceDrawer(res.data.sections['cart-drawer']);
      if (!opts.silent) { openDrawer(opts.trigger); toast(opts.title || STR.added || 'Ajouté au panier', opts.sub); }
      return res;
    } catch (err) {
      toast(STR.addError || 'Erreur panier');
      return null;
    }
  };

  const changeLine = async (line, quantity) => {
    const res = await cartRequest(`${routes.cart_change}.js`, { line, quantity });
    if (res.ok && res.data.sections?.['cart-drawer']) replaceDrawer(res.data.sections['cart-drawer']);
    return res;
  };

  /* ═══ MENU MOBILE ═══ */
  const openMenu = (trigger) => {
    const m = qs('[data-mobile-menu]');
    if (!m) return;
    lastTrigger = trigger || document.activeElement;
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    qs('[data-menu-open]')?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('body-lock');
    setTimeout(() => trapFocus(m), 60);
  };
  const closeMenu = () => {
    const m = qs('[data-mobile-menu]');
    if (!m || !m.classList.contains('is-open')) return;
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    qs('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('body-lock');
    releaseFocus();
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus({ preventScroll: true });
  };
  /* Le menu vit dans la section header (transformée au scroll) : on le
     rattache au body pour que son position:fixed couvre tout l'écran. */
  const detachMenu = () => {
    const m = qs('[data-mobile-menu]');
    if (m && m.parentElement !== document.body) document.body.appendChild(m);
  };

  /* ═══ DÉLÉGATION D'ÉVÉNEMENTS ═══ */
  document.addEventListener('click', (e) => {
    const t = e.target;
    let el;

    if ((el = t.closest('[data-cart-open]'))) { e.preventDefault(); openDrawer(el); return; }
    if (t.closest('[data-cart-close]') || t.matches('[data-cart-overlay]')) { closeDrawer(); return; }

    if ((el = t.closest('[data-qty-change]'))) {
      const line = parseInt(el.getAttribute('data-line'), 10);
      const qty = parseInt(el.getAttribute('data-qty'), 10);
      el.disabled = true;
      changeLine(line, qty).then(() => { if (document.body.classList.contains('template-cart')) window.location.reload(); });
      return;
    }

    if ((el = t.closest('[data-add-variant]'))) {
      e.preventDefault();
      const vid = parseInt(el.getAttribute('data-add-variant'), 10);
      const q = parseInt(el.getAttribute('data-add-qty') || '1', 10);
      if (!vid) return;
      el.classList.add('is-loading');
      addItem(vid, q, { title: el.getAttribute('data-add-title') || undefined, trigger: el })
        .then(() => { el.classList.remove('is-loading'); if (document.body.classList.contains('template-cart')) window.location.reload(); });
      return;
    }

    if ((el = t.closest('[data-menu-open]'))) { openMenu(el); return; }
    if (t.closest('[data-menu-close]')) { closeMenu(); return; }
    if (t.closest('[data-mobile-menu] a')) { closeMenu(); return; }

    if ((el = t.closest('[data-sticky-add]'))) {
      e.preventDefault();
      const box = qs('mf-buybox');
      if (box) box.submit(el);
      return;
    }

    if ((el = t.closest('[data-thumb]'))) {
      const gal = el.closest('[data-gallery]');
      if (!gal) return;
      const main = qs('[data-main-img]', gal);
      const box = qs('[data-gallery-main]', gal);
      if (main) {
        box?.classList.add('is-switching');
        setTimeout(() => {
          main.src = el.getAttribute('data-src');
          if (el.getAttribute('data-srcset')) main.srcset = el.getAttribute('data-srcset');
          main.alt = el.getAttribute('data-alt') || main.alt;
          const done = () => box?.classList.remove('is-switching');
          if (main.complete) done(); else main.addEventListener('load', done, { once: true });
        }, 180);
      }
      for (const th of qsa('[data-thumb]', gal)) {
        th.classList.toggle('is-active', th === el);
        th.setAttribute('aria-current', th === el ? 'true' : 'false');
      }
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeDrawer(); closeMenu(); }
  });

  /* ═══ BUYBOX 1 / 2 / 3 — Web Component ═══
     Prix calculé côté client depuis product.price + règle « chaque 2ᵉ à -X % ».
     La remise réelle est appliquée par Shopify au panier (remise automatique BXGY). */
  class MfBuybox extends HTMLElement {
    connectedCallback() {
      this.price = parseInt(this.dataset.price, 10) || 0;
      this.percent = cfg.offerEnabled === false ? 0 : (parseInt(this.dataset.percent, 10) || 0);
      this.form = this.querySelector('form');
      this.idInput = this.form?.querySelector('[name="id"]');
      this.btn = this.form?.querySelector('[data-atc]');
      this.live = this.querySelector('[data-live]');
      this.addEventListener('change', (e) => { if (e.target.matches('input[type="radio"]')) this.update(); });
      this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      this.update(true);
    }

    /** Total pour n unités, avec chaque 2ᵉ unité remisée. */
    total(n) {
      const discounted = Math.floor(n / 2);
      return n * this.price - Math.round(discounted * this.price * this.percent / 100);
    }

    get qty() {
      const checked = this.querySelector('input[type="radio"]:checked');
      return checked ? parseInt(checked.value, 10) || 1 : 1;
    }

    update(initial) {
      const n = this.qty;
      const total = this.total(n);
      const saved = n * this.price - total;
      for (const el of qsa('[data-total]', this)) el.textContent = formatMoney(total);
      for (const el of qsa('[data-saved]', this)) {
        el.hidden = saved <= 0;
        el.innerHTML = (STR.save || 'Vous économisez __P__').replace('__P__', `<b>${formatMoney(saved)}</b>`);
      }
      for (const el of qsa('[data-sep]', this)) el.hidden = saved <= 0;
      for (const el of qsa('[data-install]', this)) {
        el.innerHTML = (STR.installments || 'ou 3 × __P__ sans frais').replace('__P__', formatMoney(total / 3));
      }
      const label = this.dataset['opt' + n] || '';
      const sticky = qs('[data-sticky-price]');
      if (sticky) sticky.textContent = formatMoney(total);
      const stickyL = qs('[data-sticky-label]');
      if (stickyL && label) stickyL.textContent = `${label} · ${STR.trialShort || '30 nuits'}`;
      if (!initial && this.live) {
        this.live.textContent = '';
        requestAnimationFrame(() => { this.live.textContent = `${label} — ${formatMoney(total)}`; });
      }
    }

    async submit(trigger) {
      if (!this.idInput || !this.idInput.value) return;
      if (this.btn?.disabled) return;
      this.btn?.classList.add('is-loading');
      const n = this.qty;
      const sub = `${n} ${n > 1 ? (STR.pillows || 'oreillers') : (STR.pillow || 'oreiller')} · ${formatMoney(this.total(n))}`;
      await addItem(parseInt(this.idInput.value, 10), n, { trigger: trigger || this.btn, title: STR.added, sub });
      this.btn?.classList.remove('is-loading');
    }
  }
  if (!customElements.get('mf-buybox')) customElements.define('mf-buybox', MfBuybox);

  /* ═══ STICKY ATC (mobile) ═══ */
  const initStickyAtc = () => {
    const sticky = qs('[data-sticky-atc]');
    const anchor = qs('[data-atc-anchor]');
    if (!sticky || !anchor || !('IntersectionObserver' in window)) return;
    /* La barre est sortie de la section (position:relative + z-index:1 = contexte d'empilement) : sinon les
       sections suivantes (même z-index, plus loin dans le DOM) passent PAR-DESSUS la barre fixe. */
    if (sticky.parentElement !== document.body) document.body.appendChild(sticky);
    /* Marge basse « infinie » : l'ancre est « intersectante » tant qu'elle est sous le haut
       de l'écran. Elle cesse de l'être uniquement quand elle passe AU-DESSUS → la barre
       apparaît. Un saut direct (lien d'ancre, fling) ne peut plus rater la transition. */
    const io = new IntersectionObserver((entries) => {
      sticky.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { threshold: 0, rootMargin: '0px 0px 100000px 0px' });
    io.observe(anchor);
  };

  /* ═══ HEADER : ombre + masquage à la descente ═══ */
  const initHeader = () => {
    const header = qs('[data-header]');
    if (!header) return;
    const barre = header.closest('.header-section') || header.parentElement;
    let lastY = window.scrollY;
    const SEUIL = 120;
    const update = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      document.body.classList.toggle('is-scrolled-page', y > 8);
      const delta = y - lastY;
      if (Math.abs(delta) > 6) {
        const locked = document.body.classList.contains('body-lock');
        barre.classList.toggle('is-hidden', delta > 0 && y > SEUIL && !locked);
        lastY = y;
      }
      if (y <= SEUIL) barre.classList.remove('is-hidden');
    };
    window.addEventListener('scroll', update, { passive: true });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) { lastY = window.scrollY; update(); } });
    update();
  };

  /* ═══ ANNONCE : rotation des messages sur mobile ═══ */
  const initAnnounce = () => {
    for (const bar of qsa('[data-announce]')) {
      if (bar.dataset.ready === '1') continue;
      bar.dataset.ready = '1';
      const items = qsa('.announce__item', bar);
      if (items.length < 2) continue;
      let i = 0;
      items[0].classList.add('is-active');
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 5000);
    }
  };

  /* ═══ RÉVÉLATIONS ═══ */
  const initReveals = () => {
    const els = qsa('.reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || reduced) { for (const el of els) el.classList.add('is-in'); return; }
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      }
    }, { threshold: 0.1, rootMargin: '0px 0px -6% 0px' });
    for (const el of els) io.observe(el);
  };

  /* ═══ ACCORDÉONS : un seul ouvert par groupe (optionnel) ═══ */
  const initAccordions = () => {
    for (const group of qsa('[data-acc-single]')) {
      group.addEventListener('toggle', (e) => {
        const d = e.target;
        if (!d.open || !d.matches('details')) return;
        for (const other of qsa('details[open]', group)) if (other !== d) other.open = false;
      }, true);
    }
  };

  /* ═══ INIT ═══ */
  const init = () => {
    detachMenu();
    initHeader();
    initAnnounce();
    initReveals();
    initAccordions();
    initStickyAtc();
    syncCount();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', () => { detachMenu(); initAnnounce(); initReveals(); initStickyAtc(); });

  window.MFCart = { open: openDrawer, close: closeDrawer, add: addItem, toast };
})();
